import {
	ApiError,
	type Client,
	type NotificationChannel,
	type NotificationPreferenceLevel,
	type NotificationPreferences,
	type NotificationPreferencesInput,
} from "@khazzin/api-client";
import type { Translate } from "@khazzin/i18n";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Button } from "../primitives/Button";
import { Tooltip } from "../primitives/Display";
import { Field } from "../primitives/Field";
import { Input } from "../primitives/Input";
import { Select } from "../primitives/Select";
import { Switch } from "../primitives/Selection";
import { SkeletonCard } from "../primitives/Skeleton";
import { ErrorState } from "./page";

/**
 * Notification preferences (EP12-S16, DA-12-11): categories by channels, each
 * a switch, and quiet hours — for a person, a store's defaults, or the
 * organisation's.
 *
 * **A save sends the scope's whole row.** The server answers the effective
 * matrix with each cell's source; the cells whose source is this scope's own
 * level are the row, so a toggle is that row with one cell changed and "Reset
 * to default" is that row without the category. Nothing is kept in local state
 * that the server does not also hold.
 */

/** The cache key a scope's preferences sit under. */
export const preferencesKey = (scopeRef: string, storeId: string) =>
	["notificationPreferences", scopeRef, storeId] as const;

/** The levels, general to specific. */
const LEVELS: readonly NotificationPreferenceLevel[] = ["platform", "org", "store", "user"];

/** The level a scope sets its own cells at. */
export function levelOf(scopeRef: string): NotificationPreferenceLevel {
	const level = scopeRef.split(":")[0];
	return level === "org" || level === "store" ? level : "user";
}

/** The body a view stands for: its own cells and its own window. */
export function ownInput(view: NotificationPreferences): NotificationPreferencesInput {
	const own = levelOf(view.scopeRef);
	const channels: Record<string, Record<string, boolean>> = {};
	for (const c of view.categories) {
		for (const cell of c.cells) {
			if (cell.source === own) {
				channels[c.category] = { ...channels[c.category], [cell.channel]: cell.on };
			}
		}
	}
	const input: NotificationPreferencesInput = { scopeRef: view.scopeRef, channels };
	if (view.storeId !== undefined) {
		input.storeId = view.storeId;
	}
	if (view.quietSource === own && view.quietHours !== undefined) {
		input.quietHours = view.quietHours;
		input.timezone = view.timezone ?? "";
	}
	return input;
}

/** The body for one cell turned on or off, or a whole category reset (`null`). */
export function toggled(
	view: NotificationPreferences,
	category: string,
	channel: NotificationChannel | null,
	next: boolean | null,
): NotificationPreferencesInput {
	const input = ownInput(view);
	const channels = { ...input.channels };
	if (channel === null || next === null) {
		delete channels[category];
	} else {
		channels[category] = { ...channels[category], [channel]: next };
	}
	return { ...input, channels };
}

/** Every channel any category has, in the order the rows use them. */
function columnsOf(view: NotificationPreferences): NotificationChannel[] {
	const out: NotificationChannel[] = [];
	for (const c of view.categories) {
		for (const cell of c.cells) {
			if (!out.includes(cell.channel)) {
				out.push(cell.channel);
			}
		}
	}
	return out;
}

function zones(fallback: string): { value: string; label: string }[] {
	let all: string[] = [];
	try {
		all = Intl.supportedValuesOf("timeZone");
	} catch {
		all = [];
	}
	if (!all.includes(fallback)) {
		all = [fallback, ...all];
	}
	return all.map((z) => ({ value: z, label: z }));
}

export interface PreferenceMatrixProps {
	view: NotificationPreferences;
	t: Translate;
	editable: boolean;
	busy?: boolean;
	/** The refusal of the last save, mapped to the cell or field it names. */
	error?: { field: string; message: string } | null;
	/** The zone a new window starts in: the person's own. */
	defaultTimezone: string;
	onSave: (input: NotificationPreferencesInput) => void;
}

export function PreferenceMatrix({
	view,
	t,
	editable,
	busy = false,
	error = null,
	defaultTimezone,
	onSave,
}: PreferenceMatrixProps) {
	const own = levelOf(view.scopeRef);
	const columns = columnsOf(view);
	const quietOwn = view.quietSource === own;
	const [start, end] = (view.quietHours ?? "22:00-07:00").split("-");
	const [from, setFrom] = useState(start ?? "22:00");
	const [to, setTo] = useState(end ?? "07:00");
	const [zone, setZone] = useState(view.timezone ?? defaultTimezone);
	const disabled = !editable || busy;
	const cellError = error?.field.startsWith("channels.") ? error : null;
	const quietError = error?.field === "quietHours" ? error.message : undefined;
	const zoneError = error?.field === "timezone" ? error.message : undefined;
	const gridStyle = { gridTemplateColumns: `minmax(0,1fr) repeat(${columns.length}, 5.5rem)` };

	return (
		<div className="flex flex-col gap-4" aria-busy={busy || undefined}>
			<div className="flex flex-col">
				<div
					aria-hidden
					className="hidden gap-2 border-b border-border pb-2 sm:grid"
					style={gridStyle}
				>
					<span className="text-[12px] font-semibold text-fg-muted">
						{t("preferences.notifications.category")}
					</span>
					{columns.map((ch) => (
						<span key={ch} className="text-center text-[12px] font-semibold text-fg-muted">
							{t(`preferences.notifications.channel.${ch}`)}
						</span>
					))}
				</div>
				{view.categories.map((c) => {
					const name = t(`preferences.notifications.categories.${c.category}`);
					const set = c.cells.some((cell) => cell.source === own);
					// The nearest level any of the row's cells comes from.
					const inherited = c.cells.reduce<NotificationPreferenceLevel>(
						(best, cell) =>
							LEVELS.indexOf(cell.source) > LEVELS.indexOf(best) ? cell.source : best,
						"platform",
					);
					return (
						<div
							key={c.category}
							className="flex flex-col gap-2 border-b border-border py-3 sm:grid sm:items-center"
							style={gridStyle}
						>
							<div className="min-w-0">
								<div className="text-[13px] font-semibold text-fg">{name}</div>
								<div className="text-[12px] text-fg-subtle">
									{c.mandatory ? (
										t("preferences.notifications.required")
									) : set && editable ? (
										<button
											type="button"
											className="text-accent underline-offset-2 hover:underline"
											disabled={disabled}
											onClick={() => onSave(toggled(view, c.category, null, null))}
										>
											{t("preferences.notifications.reset")}
										</button>
									) : (
										t("preferences.notifications.inherited", {
											source: t(`preferences.notifications.source.${inherited}`),
										})
									)}
								</div>
							</div>
							{columns.map((ch) => {
								const cell = c.cells.find((x) => x.channel === ch);
								const label = `${name} · ${t(`preferences.notifications.channel.${ch}`)}`;
								return (
									<div
										key={ch}
										className="flex items-center justify-between gap-2 sm:justify-center"
									>
										<span className="text-[12.5px] text-fg-muted sm:sr-only">
											{t(`preferences.notifications.channel.${ch}`)}
										</span>
										{cell === undefined ? (
											<span className="text-fg-subtle">
												<span aria-hidden>—</span>
												<span className="sr-only">{t("preferences.notifications.none")}</span>
											</span>
										) : c.mandatory ? (
											<Tooltip content={t("preferences.notifications.locked")}>
												<span>
													<Switch aria-label={label} checked disabled />
												</span>
											</Tooltip>
										) : (
											<Switch
												aria-label={label}
												checked={cell.on}
												disabled={disabled}
												onCheckedChange={(next) => onSave(toggled(view, c.category, ch, next))}
											/>
										)}
									</div>
								);
							})}
						</div>
					);
				})}
			</div>
			{cellError !== null && (
				<p role="alert" className="text-[12.5px] text-danger-fg">
					{cellError.message}
				</p>
			)}

			<form
				className="flex flex-wrap items-end gap-3"
				onSubmit={(event) => {
					event.preventDefault();
					onSave({ ...ownInput(view), quietHours: `${from}-${to}`, timezone: zone });
				}}
			>
				<div className="basis-full">
					<div className="text-[13px] font-semibold text-fg">
						{t("preferences.notifications.quiet.title")}
					</div>
					<p className="text-[12px] text-fg-subtle">
						{view.quietHours === undefined
							? t("preferences.notifications.quiet.none")
							: quietOwn
								? t("preferences.notifications.quiet.set", {
										window: view.quietHours,
										zone: view.timezone ?? "",
									})
								: t("preferences.notifications.quiet.inherited", {
										window: view.quietHours,
										zone: view.timezone ?? "",
										source: t(`preferences.notifications.source.${view.quietSource ?? "platform"}`),
									})}{" "}
						{t("preferences.notifications.quiet.critical")}
					</p>
				</div>
				<Field label={t("preferences.notifications.quiet.from")} error={quietError}>
					{(control) => (
						<Input
							{...control}
							type="time"
							dir="ltr"
							disabled={disabled}
							value={from}
							onChange={(event) => setFrom(event.target.value)}
						/>
					)}
				</Field>
				<Field label={t("preferences.notifications.quiet.to")}>
					{(control) => (
						<Input
							{...control}
							type="time"
							dir="ltr"
							disabled={disabled}
							value={to}
							onChange={(event) => setTo(event.target.value)}
						/>
					)}
				</Field>
				<Field label={t("preferences.notifications.quiet.timezone")} error={zoneError}>
					{(control) => (
						<Select
							{...control}
							options={zones(zone)}
							value={zone}
							disabled={disabled}
							onValueChange={setZone}
						/>
					)}
				</Field>
				{editable && (
					<div className="flex gap-2">
						<Button type="submit" size="sm" disabled={busy}>
							{t("preferences.notifications.quiet.save")}
						</Button>
						{quietOwn && (
							<Button
								type="button"
								size="sm"
								variant="ghost"
								disabled={busy}
								onClick={() => {
									const { quietHours: _q, timezone: _z, ...rest } = ownInput(view);
									onSave(rest);
								}}
							>
								{t("preferences.notifications.quiet.clear")}
							</Button>
						)}
					</div>
				)}
			</form>
		</div>
	);
}

export interface NotificationPreferencesCardProps {
	api: Client;
	t: Translate;
	title: string;
	subtitle?: string;
	/** Absent is the caller's own. */
	scopeRef?: string;
	/** The store whose defaults a person's view includes. */
	storeId?: string;
	editable?: boolean;
	defaultTimezone: string;
}

/** The matrix over the API: read, save, and the save's refusal mapped back. */
export function NotificationPreferencesCard({
	api,
	t,
	title,
	subtitle,
	scopeRef,
	storeId,
	editable = true,
	defaultTimezone,
}: NotificationPreferencesCardProps) {
	const queries = useQueryClient();
	const key = preferencesKey(scopeRef ?? "", storeId ?? "");
	const query: { scopeRef?: string; storeId?: string } = {};
	if (scopeRef !== undefined) {
		query.scopeRef = scopeRef;
	}
	if (storeId !== undefined) {
		query.storeId = storeId;
	}
	const view = useQuery({
		queryKey: key,
		queryFn: ({ signal }) => api.getNotificationPreferences(query, { signal }),
	});
	const save = useMutation({
		mutationFn: (input: NotificationPreferencesInput) => api.putNotificationPreferences(input),
		onSuccess: (next) => {
			queries.setQueryData(key, next);
			void queries.invalidateQueries({ queryKey: ["notificationPreferences"] });
		},
	});
	const error =
		save.error instanceof ApiError && save.error.field !== null
			? { field: save.error.field, message: save.error.message }
			: null;

	return (
		<section aria-label={title} className="flex flex-col gap-3 card p-4">
			<div>
				<h2 className="text-[14px] font-bold text-fg">{title}</h2>
				{subtitle !== undefined && <p className="text-[12px] text-fg-subtle">{subtitle}</p>}
			</div>
			{view.isPending ? (
				<SkeletonCard />
			) : view.isError ? (
				<ErrorState t={t} error={view.error} compact onRetry={() => void view.refetch()} />
			) : (
				<>
					<PreferenceMatrix
						key={`${view.data.quietHours ?? ""}|${view.data.timezone ?? ""}`}
						view={view.data}
						t={t}
						editable={editable}
						busy={save.isPending}
						error={error}
						defaultTimezone={defaultTimezone}
						onSave={(input) => save.mutate(input)}
					/>
					{save.isError && error === null && <ErrorState t={t} error={save.error} compact />}
				</>
			)}
		</section>
	);
}
